import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { cores } from '../../theme';
import {
    Typography, Container, Box, Paper, TextField, Button,
    Alert, FormControlLabel, Checkbox, CircularProgress,
    IconButton, Fade, Snackbar, useTheme
} from '@mui/material';
import { ArrowBack, Save as SaveIcon, AdminPanelSettings as AdminIcon } from '@mui/icons-material';

const ProfessorEditarPagina = () => {
    const { id } = useParams();
    const navegar = useNavigate();
    const theme = useTheme();
    const [nome, setNome] = useState('');
    const [email, setEmail] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [sucesso, setSucesso] = useState('');

    useEffect(() => {
        const buscarProfessor = async () => {
            try {
                const resposta = await api.get(`/api/usuarios/${id}`);
                const professor = resposta.data;
                setNome(professor.nome_completo);
                setEmail(professor.email);
                setIsAdmin(professor.is_admin);
            } catch (err) {
                setErro("Não foi possível carregar os dados do professor.");
            } finally {
                setCarregando(false);
            }
        };
        buscarProfessor();
    }, [id]);

    const lidarComSubmissao = async (e) => {
        e.preventDefault();
        setErro('');
        setSucesso('');
        try {
            const dadosAtualizados = {
                nome_completo: nome,
                email: email,
                is_admin: isAdmin,
            };
            await api.put(`/api/usuarios/${id}`, dadosAtualizados);
            setSucesso('Professor atualizado com sucesso!');
            setTimeout(() => navegar('/gerenciar-professores'), 1500);
        } catch (err) {
            setErro(err.response?.data?.erro || 'Ocorreu um erro ao atualizar o professor.');
        }
    };

    if (carregando) {
        return (
            <Container maxWidth="lg" sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: cores.primaria.principal }} />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
            <Fade in={true} timeout={500}>
                <Paper sx={{
                    p: { xs: 2, sm: 4 },
                    borderRadius: 3,
                    boxShadow: cores.sombras.medio
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 4,
                        p: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        background: cores.gradientes.primario,
                        color: 'white'
                    }}>
                        <IconButton
                            onClick={() => navegar('/gerenciar-professores')}
                            sx={{
                                mr: 2,
                                color: 'white',
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                }
                            }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                Editar Professor
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    opacity: 0.9,
                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                }}
                            >
                                Atualize as informações do professor
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        component="form"
                        onSubmit={lidarComSubmissao}
                        sx={{
                            p: { xs: 0, sm: 2 },
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2
                        }}
                    >
                        <TextField
                            label="Nome Completo"
                            variant="outlined"
                            fullWidth
                            required
                            value={nome}
                            onChange={(e) => setNome(e.target.value)}
                            InputProps={{
                                sx: { borderRadius: 2 }
                            }}
                            helperText="Digite o nome completo do professor"
                        />

                        <TextField
                            label="Email Institucional"
                            type="email"
                            variant="outlined"
                            fullWidth
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            InputProps={{
                                sx: { borderRadius: 2 }
                            }}
                            helperText="Este email será usado para acesso ao sistema"
                        />

                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                border: `1px solid ${cores.cinza.claro}`,
                                borderRadius: 2,
                                bgcolor: isAdmin ? `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)` : 'transparent',
                                transition: 'background-color 0.2s ease'
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isAdmin}
                                        onChange={(e) => setIsAdmin(e.target.checked)}
                                        icon={<AdminIcon sx={{ color: cores.cinza.medio }} />}
                                        checkedIcon={<AdminIcon sx={{ color: cores.primaria.principal }} />}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography sx={{ fontWeight: 600, color: isAdmin ? cores.primaria.principal : cores.cinza.escuro }}>
                                            Conceder privilégios de administrador
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: cores.cinza.medio }}>
                                            Administradores podem gerenciar todos os recursos do sistema
                                        </Typography>
                                    </Box>
                                }
                                sx={{ m: 0 }}
                            />
                        </Paper>

                        {erro && (
                            <Alert
                                severity="error"
                                sx={{
                                    mt: 2,
                                    borderRadius: 2,
                                    backgroundColor: cores.erro.claro,
                                    borderColor: cores.erro.medio,
                                    color: cores.erro.escuro,
                                    '& .MuiAlert-icon': {
                                        alignItems: 'center'
                                    }
                                }}
                            >
                                {erro}
                            </Alert>
                        )}

                        <Box sx={{
                            mt: 4,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Button
                                variant="outlined"
                                onClick={() => navegar('/gerenciar-professores')}
                                sx={{
                                    borderRadius: 2,
                                    py: 1.5,
                                    px: 3,
                                    borderColor: cores.cinza.medio,
                                    color: cores.cinza.escuro,
                                    '&:hover': {
                                        borderColor: cores.cinza.escuro,
                                        bgcolor: 'rgba(71, 85, 105, 0.04)'
                                    }
                                }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={<SaveIcon />}
                                sx={{
                                    borderRadius: 2,
                                    py: 1.5,
                                    px: 3,
                                    bgcolor: cores.primaria.principal,
                                    fontWeight: 600,
                                    boxShadow: cores.sombras.suave,
                                    '&:hover': {
                                        bgcolor: cores.primaria.escuro,
                                    }
                                }}
                            >
                                Salvar Alterações
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Fade>

            <Snackbar
                open={!!sucesso}
                autoHideDuration={4000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    severity="success"
                    variant="filled"
                    sx={{
                        width: '100%',
                        boxShadow: cores.sombras.medio,
                        backgroundColor: cores.sucesso.principal
                    }}
                >
                    {sucesso}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default ProfessorEditarPagina;